import { ChangeDetectionStrategy, Component, computed, effect, inject, Signal, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Button } from 'primeng/button';
import { InputGroup } from "primeng/inputgroup";
import { InputGroupAddon } from "primeng/inputgroupaddon";
import { InputNumber } from "primeng/inputnumber";
import { Tooltip } from "primeng/tooltip";
import { combineLatest, finalize, of, switchMap } from 'rxjs';
import { CustomerAverageAnnualDeliveredUnitsService } from '../../../delivery';
import { ReportParameterBase } from './report-parameter-base';

@Component({
    selector: 'app-customer-average-annual-delivered-units-parameter',
    imports: [Button, FormsModule, InputGroup, InputNumber, InputGroupAddon, Tooltip],
    template: `
        <p-input-group >
            <p-input-number 
                [ngModel]="value()"
                [disabled]="isDisabled()"
                [invalid]="isInvalid()"
                class="p-invalid"
                (onBlur)="onBlur()"
                (ngModelChange)="onValueChange($event)"
                 />
            <p-inputgroup-addon>
                <p-button 
                    icon="pi pi-refresh" 
                    severity="secondary" 
                    [disabled]="serverValue() == null"
                    (onClick)="onResetToInitial()" 
                    [pTooltip]="resetToolTipMessage()">
                </p-button>
            </p-inputgroup-addon>
        </p-input-group>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: CustomerAverageAnnualDeliveredUnitsParameterComponent,
            multi: true
        }
    ]
})
export class CustomerAverageAnnualDeliveredUnitsParameterComponent extends ReportParameterBase<number | null> {
    private customerAverageAnnualDeliveredUnitsService = inject(CustomerAverageAnnualDeliveredUnitsService);

    subscriberKey: Signal<number | null> = this.reportWorkspace.subscriberKey;
    divisionHierarchyNodeKeys: Signal<number[]> = this.reportWorkspace.getParamSignal('divisionHierarchyNodeKeys');
    productCategoryCostKey: Signal<number | null> = this.reportWorkspace.getParamSignal('productCategoryCostKey');
    
    value = signal<number | null>(null);

    private initialValue = this.reportWorkspace.parameterValues()['customerAverageAnnualDeliveredUnits'];
    valueSource: 'calculated' | 'manual' = this.initialValue ? 'manual' : 'calculated';

    isServerValueLoading = signal<boolean>(false);

    resetToolTipMessage = computed(() => {
        const serverValue = this.serverValue();

        if (serverValue == null) {
            return undefined;
        }

        return `Reset to calculated value: ${serverValue.toLocaleString()}`;
    });

    /* Override Disabled */
    override isDisabled = computed(() => this.reportWorkspace.isReportRunning() || this.isFormControlDisabled() || this.isServerValueLoading() );

    serverValue = toSignal(
        combineLatest({
            subscriberKey: toObservable(this.subscriberKey),
            divisionHierarchyNodeKeys: toObservable(this.divisionHierarchyNodeKeys),
            productCategoryCostKey: toObservable(this.productCategoryCostKey ),
        }).pipe(
            switchMap(({subscriberKey, divisionHierarchyNodeKeys, productCategoryCostKey}) => {
                if (subscriberKey == null || productCategoryCostKey == null) {
                    return of(null);
                }

                return this.getServerValue(subscriberKey, productCategoryCostKey, divisionHierarchyNodeKeys ?? [])
            })
        ),
        {initialValue: null}
    );

    constructor() {
        super();

        /* Update Value whenever Server Value changes */
        effect(() => {
            const serverValue = this.serverValue() ?? null;

            if (this.valueSource === 'calculated') {
                this.setValue(serverValue);
            }
        });
    }

    getServerValue(subscriberKey: number, productCategoryCostKey: number, hierarchyNodeKeys: number[]) {
        this.reportWorkspace.setParamLoading(this.parameterKey(), true);
        this.isServerValueLoading.set(true);

        return this.customerAverageAnnualDeliveredUnitsService
            .get(subscriberKey, productCategoryCostKey, hierarchyNodeKeys)
            .pipe(
                finalize(() => {
                    this.isServerValueLoading.set(false);
                    this.reportWorkspace.setParamLoading(this.parameterKey(), false);
                })
            );
    }

    private setValue(value: number | null) {  
        this.value.set(value);        
        this.reportWorkspace.setParam(this.parameterKey(), value);
        this.onChange(value);
    }

    onValueChange(value: number | null) {
        this.valueSource = 'manual';
        this.setValue(value);
        this.markTouchedIfNotResetting();
    }

    onResetToInitial() {
        this.valueSource = 'calculated';
        this.setValue(this.serverValue());
    }

    onBlur() {
        this.onTouched();
    }

    // CVA callbacks
    writeValue(obj: number | null): void {
        this.value.set(obj);
        if (this.isInitialized) {
            this.reportWorkspace.setParam(this.parameterDefinition().key, obj);
        }
    }
}
